import { Dialog } from "primereact/dialog"
import { InvoiceTracking } from "./Table"
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVInput } from "../../shared/JVInput";
import { Button } from "primereact/button";
import { Fetch } from "../../shared/Fetch";
import { ICompanyMast } from "../../models/CompanyMast";

interface IProps {
    tableData: InvoiceTracking[],
    openSendEmail: boolean,
    closeSendEmailModal: () => void,
    toast: any
}

interface ISendEmailForPaymentUpdate {
    senderEmail?: string | null;
    subject?: string | null;
    body?: string | null;
}

const schema = yup.object({
    // senderEmail: yup.string().required("Sender Email is required"),
    subject: yup.string().nullable(),
    body: yup.string().nullable(),
});

const demoTemplates = [
    {
        subject: `Reminder: Pending Payment for Consultant Services`,
        body: `<p>Dear @PV_Name,<p/>
               <br/>
               <p>I hope this message finds you well. I wanted to kindly remind you that the payment for the Invoice No @Invoice_Number and the consultant @Consultant_Name services provided, as per our agreement, was due on @Invoice_Due_Date. As of today, we have not yet received the payment.<p/>
               <br/>
               <p>We would appreciate it if you could process the payment at your earliest convenience. Please let us know if you need any additional information from our side to facilitate this.<p/>
               <br/>
               <p>Thank you for your prompt attention to this matter.<p/>
               <br/>
               <p>Best regards,<p/>
               @signator`
    },
    {
        subject: `Follow-Up on Overdue Payment for Consultant`,
        body: `<p>Hi @PV_Name,<p/>
               <br/>
               <p>I hope you’re doing well. This is a friendly follow-up regarding the payment for the Invoice No @Invoice_Number and the consultant @Consultant_Name services, which was due on @Invoice_Due_Date. We haven’t received the payment yet and wanted to check if there’s anything we can assist with to help expedite the process.<p/>
               <br/>
               <p>Looking forward to your response.<p/>
               <br/>
               <p>Best,<p/>
               <br/>
               @signator`
    },
    {
        subject: `Overdue Payment Notice – Consultant Invoice`,
        body: `<p>Dear @PV_Name,<p/>
               <br/>
               <p>This is to formally notify you that the payment for the consultant @Consultant_Name whose Invoice Number @Invoice_Number, originally due on @Invoice_Due_Date, remains outstanding.<p/>
               <br/>
               <p>Kindly arrange for the payment as soon as possible to avoid any disruption to ongoing services. Please confirm receipt of this email and advise on the expected payment date.<p/>
               <br/>
               <p>Thank you for your cooperation.<p/>
               <br/>
               <p>Sincerely,<p/>
               <br/>
               @signator`
    },
    {
        subject: `Urgent: Payment Overdue for Consultant Services`,

        body: `<p>Dear @PV_Name,<p/>
               <br/>
               <p>We would like to bring to your immediate attention that the payment for the consultant @Consultant_Name whose Invoice No @Invoice_Number services due on @Invoice_Due_Date has not yet been received. Timely payment is essential to maintain the smooth continuation of services.<p/>
               <br/>
               <p>Please arrange for payment at the earliest and confirm once done. If there are any issues or queries, feel free to reach out.<p/>
               <br/>
               <p>Thank you for your prompt action.<p/>
               <br/>
               <p>Kind regards,<p/>
               <br/>
               @signator`
    },
    {
        subject: `Final Reminder: Outstanding Payment for Consultant Services`,
        body: `Dear @PV_Name,
               <br/>
               This is a reminder regarding the overdue payment for the consultant @Consultant_Name whose Invoice No @Invoice_Number services provided, with the due date on @Invoice_Due_Date. Despite previous reminders, the payment remains outstanding.
               <br/>
               We urge you to process the payment immediately to avoid further escalation. Please confirm the payment status at your earliest convenience.
               <br/>
               Thank you for your attention.
               <br/>
               Best regards,
               <br/>
               @signator`
    }
];

export const SendEmailForPaymentUpdate = (props: IProps) => {
    const [emailDescription, setEmailDescription] = useState("");
    const [senderEmail, setSenderEmail] = useState([] as ICompanyMast[]);
    const [emailTemplates, setEmailTemplates] = useState([] as { subject: string, body: string }[]);
    const [emailTemplateIndex, setEmailTemplateIndex] = useState<number>(1);
    const [loading, setLoading] = useState(false);
    const { control, handleSubmit, formState: { errors }, reset, watch, setValue, setError, setFocus } = useForm<ISendEmailForPaymentUpdate>({
        resolver: yupResolver(schema),
        defaultValues: {},
        shouldFocusError: false
    });

    const onSubmit = async (body: ISendEmailForPaymentUpdate) => {
        setLoading(true);
        const newEmailSendingBody = { ...body, body: emailDescription, invoiceIDs: props.tableData.map(item => item.invoice_ID) };
        const response = await Fetch("InvoiceTracking/sendEmailForPaymentUpdate", "POST", newEmailSendingBody, null, props.toast);
        if (response) {
            props.toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Email sent successfully!', life: 3000 });
            onHide();
        }
        setLoading(false);
    };

    const loadEmailTemplate = async () => {
        setEmailTemplates(prev => prev = demoTemplates);
        setValue("subject", emailTemplates[0].subject);
        setEmailDescription(emailTemplates[0].body);
    };

    const onClear = () => {
        reset();
        setLoading(false);
        setEmailTemplateIndex(0);
        setEmailDescription("");
        setValue("senderEmail", null);
        setValue("subject", null);
        setValue("body", null);
    }

    const onHide = () => {
        setEmailDescription("");
        setEmailTemplateIndex(0);
        onClear();
        setLoading(false);
        props.closeSendEmailModal();
    };

    useEffect(() => {
        if (emailTemplates.length > 0 && props.openSendEmail) {
            setEmailTemplateIndex(0);
            setValue("subject", emailTemplates[0].subject);
            setEmailDescription(emailTemplates[0].body);
        }
    }, [emailTemplates]);

    useEffect(() => {
        if (props.openSendEmail) {
            loadEmailTemplate();
        }
    }, [props.openSendEmail]);

    return (
        <>
            <Dialog style={{ width: '70vw' }} visible={props.openSendEmail} onHide={() => onHide()} header="Send Emai For Payment Update On Invoices" >
                <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                    {/* <JVDropdown required column={3} label="Select Sender Email" control={control} name="senderEmail" error={errors.senderEmail?.message} options={senderEmail} optionLabel="company_Domain" optionValue="company_Domain" placeholder="Select Sender Email" /> */}
                    <JVInput required column={3} label="Subject" control={control} name="subject" error={errors.subject?.message} />
                    <div className="col-12 md:col-12 md:col-6 flex gap-3">
                        {emailTemplates.map((item, index) => <Button type="button" label={`Template ${index + 1}`} onClick={() => {
                            setEmailTemplateIndex(index + 1);
                            setValue("subject", emailTemplates[index].subject);
                            setEmailDescription(emailTemplates[index].body);
                        }} />)}
                    </div>
                    <div className="col-12 md:col-12 md:col-6 mt-3">
                        <label htmlFor="body">Body<span style={{ color: 'red' }}>*</span></label>
                        <CKEditor editor={ClassicEditor} data={emailDescription} onChange={(event, editor) => { setEmailDescription(editor.getData()); }} />
                    </div>
                    <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                        <Button loading={loading} disabled={loading} label="SEND" type={loading === false ? "submit" : "button"} style={{ width: '100px' }} ></Button>
                        <Button className="p-button-outlined surface-200" style={{ width: '100px' }} label="CLEAR" onClick={onClear} type="button" ></Button>
                    </div>
                </form>
            </Dialog >
        </>
    )
}